import { Injectable } from '@angular/core';
import { AuthService } from '@core/services/auth.service';
import { UserService } from '@core/services/user.service';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, from, MonoTypeOperatorFunction, Observable, pipe, throwError } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';

interface ICreatePasswordState {
  passwordCreated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: ICreatePasswordState = {
  error: null,
  loading: false,
  passwordCreated: false,
};

@Injectable()
export class CreatePasswordStore extends ComponentStore<ICreatePasswordState> {
  public readonly passwordCreated$ = this.selectSignal((state: ICreatePasswordState) => state.passwordCreated);
  public readonly loading$ = this.selectSignal((state: ICreatePasswordState) => state.loading);
  public readonly error$ = this.selectSignal((state: ICreatePasswordState) => state.error);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          error: errorMessage,
          loading: false,
        });

        return EMPTY;
      }),
    );

  public readonly createPassword = this.effect((trigger$: Observable<{ password: string }>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, loading: true, passwordCreated: false })),
      switchMap(({ password }) =>
        from(this.authService.updatePassword(password)).pipe(
          // On recent-login requirement, fetch a fresh custom token, re-auth, and retry once
          catchError((error: unknown) => {
            if (this.isRecentLoginError(error)) {
              return this.userService.getCustomToken().pipe(
                take(1),
                switchMap((token: string) => from(this.authService.signInWithCustomToken(token))),
                switchMap(() => from(this.authService.updatePassword(password))),
              );
            }

            return throwError(() => error);
          }),
          tap(() => this.patchState({ passwordCreated: true })),
          this.handleError('Failed to create password'),
        ),
      ),
    );
  });

  constructor(
    private readonly authService: AuthService,
    private readonly userService: UserService,
  ) {
    super(initialState);
  }

  private isRecentLoginError(error: unknown): boolean {
    const code: string | undefined = (error as { code?: string })?.code;
    const message: string | undefined = (error as { message?: string })?.message;

    return (
      !!code?.includes('requires-recent-login') ||
      !!code?.includes('CREDENTIAL_TOO_OLD_LOGIN_AGAIN') ||
      !!message?.includes('requires-recent-login') ||
      !!message?.includes('CREDENTIAL_TOO_OLD_LOGIN_AGAIN')
    );
  }
}
